import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { MessageCategoryModelDto } from '@treasury/api/channel';
import { MessagesService } from '@treasury/domain/channel/services';
import { TmContainer } from '@treasury/presentation';
import { type SelectItemVm } from '@treasury/presentation/components/forms';
import '@treasury/presentation/components/forms/tm-form-row';
import '@treasury/presentation/components/forms/tm-slider';
import '@treasury/presentation/components/tm-body';
import '@treasury/presentation/components/tm-footer';
import { ButtonConfig } from '@treasury/presentation/components/tm-footer.types';
import { TmSearchTermChangeEvent } from '@treasury/presentation/components/tm-list-header';
import '@treasury/presentation/components/tm-select';
import '@treasury/presentation/components/tm-text-area';

import { DeepReactive } from '@jack-henry/frontend-utils/lit';
// eslint-disable-next-line no-duplicate-imports
import type { TmTextAreaValueChanged } from '@treasury/presentation/components/tm-text-area';
import '@treasury/presentation/components/tm-upload';
import { css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { filterTableResults } from '../../../utilities/table-result-filter';
import { Message } from '../types/message.types';

const MessageSubjectItems = (items: MessageCategoryModelDto[]) =>
    items.map(item => ({
        value: item.fiMessageCategoryId.toString(),
        label: item.categoryDescription || '',
    }));

const defaultFormData = {
    userId: 0,
    categoryId: 0,
    parentMessageId: 0,
    assignedToUserId: 0,
    files: undefined,
    body: '',
};

export const tagName = 'compose-message-form';
@customElement(tagName)
export class ComposeMessageForm extends TmContainer {
    @property({ type: Array })
    public message: Message[] = [];

    @property({ type: Boolean })
    public isReplying = false;

    @InjectProperty()
    declare private readonly messageService: MessagesService;

    @state()
    private messageSubjectItems: SelectItemVm<string>[] = [];

    @state()
    private filteredSubjectList: SelectItemVm<string>[] = [];

    @state()
    private selectedSubject?: SelectItemVm<string>;

    @DeepReactive()
    private formData = { ...defaultFormData };

    @DeepReactive()
    private validState = {
        subject: false,
        message: false,
    };

    private messageCharLimit = 1000;

    protected async firstUpdated() {
        if (this.isReplying) {
            this.loading = false;
            // Subject input is only required/available for new messages, not when replying
            this.validState.subject = true;
        } else {
            await this.tryFetch(
                () => this.messageService.getMessageCategories(),
                messageSubjects => {
                    this.messageSubjectItems = MessageSubjectItems(messageSubjects);
                    this.filteredSubjectList = this.messageSubjectItems;
                }
            );
        }
    }

    protected updated(changedProps: Map<string, unknown>) {
        if (changedProps.has('message')) {
            if (!this.message || this.message.length < 1) {
                return;
            }

            const [firstMessage] = this.message;
            const { parentMessageId, messageId } = firstMessage;
            const formMessageId = parentMessageId || parseInt(messageId, 10) || 0;

            this.formData = {
                ...this.formData,
                userId: this.message[0].assignedToUserId ?? 0,
                categoryId: this.message[0].categoryId,
                parentMessageId: formMessageId,
            };
        }
    }

    protected render() {
        return html`
            ${this.renderLoader()}
            <tm-body .padding=${false} .fullHeight=${false}>
                <form>
                    ${this.renderSubjectField()} ${this.renderMessageField()}
                    <tm-upload
                        class="py-2 px-4"
                        .accept=${'.csv,.doc,.docx,.gif,.jfif,.jif,.jpg,.jpeg,.jpe,.pdf,.png,.txt,.xls,.xlsx'}
                        @files-changed=${(e: CustomEvent) => {
                            this.formData.files = e.detail.value;
                        }}
                    ></tm-upload>
                </form>
            </tm-body>
            <tm-footer
                class="fixed bottom-0 left-0 w-full"
                direction="row"
                .buttonConfig=${[
                    {
                        text: 'Cancel',
                        onClick: () => this.cancelMessage(),
                        importance: 'secondary',
                        classes: 'w-[33%]',
                    },
                    {
                        text: 'Send',
                        onClick: () => this.sendMessage(),
                        disabled: Object.values(this.validState).some(isValid => !isValid),
                        classes: 'w-[67%]',
                    },
                ] as ButtonConfig[]}
            >
            </tm-footer>
        `;
    }

    private sendMessage() {
        this.dispatchEvent(new CustomEvent('send-message', { detail: this.formData }));
        this.formData = { ...defaultFormData };
    }

    private cancelMessage() {
        this.dispatchEvent(new CustomEvent('cancel-message'));
        this.formData = { ...defaultFormData };
    }

    private filterResults(e: TmSearchTermChangeEvent) {
        this.filteredSubjectList = filterTableResults(e, this.messageSubjectItems, ['label']);
    }

    private handleSubjectSelect(e: Event, item: SelectItemVm<string>) {
        e.target?.dispatchEvent(
            new CustomEvent('subject-changed', {
                detail: item.value,
                bubbles: true,
            })
        );
        this.selectedSubject = item;
        this.formData.categoryId = Number(item.value);
        this.validState.subject = !!this.formData.categoryId;
        e.target?.dispatchEvent(new CustomEvent('close', { bubbles: true }));
    }

    private renderSubjectList() {
        return html`<tm-section
            ><tm-list-header
                .filterButton=${false}
                @search-term-change=${(e: TmSearchTermChangeEvent) => {
                    this.filterResults(e);
                }}
            ></tm-list-header>
            <div class="pl-4 test-subject-list">
                ${this.filteredSubjectList.map(
                    item =>
                        html`<div
                            class="text-left border-b py-3 pr-4 test-subject-item"
                            @click=${(e: Event) => this.handleSubjectSelect(e, item)}
                            @keyup=${(e: KeyboardEvent) =>
                                e.key === 'Enter' && this.handleSubjectSelect(e, item)}
                        >
                            ${item.label}
                        </div>`
                )}
            </div></tm-section
        >`;
    }

    private renderSubjectField() {
        if (this.isReplying) {
            return nothing;
        }

        return html` <tm-form-row label="Subject" class="test-subject-field" required noWrap>
            <tm-slider
                header="Select Subject"
                placeholder="Select Subject"
                .value=${this.selectedSubject?.label}
                >${this.renderSubjectList()}
            </tm-slider>
        </tm-form-row>`;
    }

    private renderMessageField() {
        return html`<tm-form-row-message-center required class="pt-4 pb-2 ml-4 pr-4">
            <div slot="label" class="pt-[14px] text-sm font-medium">Message</div>
            <tm-text-area
                slot="content"
                class="w-full text-right"
                .initial=${this.formData.body}
                .maxLength=${this.messageCharLimit}
                @value-changed=${(e: TmTextAreaValueChanged) => {
                    this.formData.body = e.detail.value || '';
                    this.validState.message = !!this.formData.body.length;
                }}
                .helperText="${`${this.formData.body.length}/${this.messageCharLimit}`}"
                .hasBottomBorder=${false}
            ></tm-text-area>
        </tm-form-row-message-center>`;
    }

    public static get styles() {
        return [
            css`
                :host {
                    display: flex;
                    flex-direction: column;
                    height: 100%;
                }

                tm-text-area {
                    min-height: 200px;
                    max-height: 400px;
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: ComposeMessageForm;
    }
}
